import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useCampaignStore } from "@/store/campaignStore";
import { EmailCampaignType } from "@/types/email";
import type { WizardData } from "@/pages/campaigns/CampaignWizard";

interface StepReviewSendProps {
  data: WizardData;
  onBack: () => void;
  onComplete: (campaignId: string) => void;
}

function getTypeLabel(type: EmailCampaignType): string {
  switch (type) {
    case EmailCampaignType.Promotional: return "Promotional";
    case EmailCampaignType.Transactional: return "Transactional";
    case EmailCampaignType.Newsletter: return "Newsletter";
    default: return type;
  }
}

export function StepReviewSend({ data, onBack, onComplete }: StepReviewSendProps) {
  const { createCampaign, scheduleCampaign, sendNow, isSaving, error } = useCampaignStore();
  const [scheduleMode, setScheduleMode] = useState(false);
  const [scheduledAt, setScheduledAt] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Holds the campaign once created so a failed send/schedule retry reuses it
  // instead of creating a duplicate draft on every click.
  const [createdCampaignId, setCreatedCampaignId] = useState<string | null>(null);

  const documentId = data.selectedDocument?.id;

  const ensureCampaign = async (): Promise<string> => {
    if (createdCampaignId) return createdCampaignId;
    const campaign = await createCampaign(buildPayload());
    setCreatedCampaignId(campaign.id);
    return campaign.id;
  };

  const buildPayload = () => ({
    name: data.name,
    type: data.type,
    subject: data.subject,
    previewText: data.previewText || undefined,
    fromName: data.fromName,
    fromEmail: data.fromEmail,
    replyToEmail: data.replyToEmail || undefined,
    recipientListJson: JSON.stringify({ listIds: data.selectedListIds }),
    estimatedRecipients: data.estimatedRecipients,
    trackOpens: data.trackOpens,
    trackClicks: data.trackClicks,
    emailDocumentId: documentId!,
  });

  const handleSaveAsDraft = async () => {
    setSubmitError(null);
    try {
      const campaignId = await ensureCampaign();
      onComplete(campaignId);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Failed to save campaign");
    }
  };

  const handleSendNow = async () => {
    setSubmitError(null);
    try {
      const campaignId = await ensureCampaign();
      await sendNow(campaignId);
      onComplete(campaignId);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Failed to send campaign");
    }
  };

  const handleSchedule = async () => {
    if (!scheduledAt) return;
    // `min` only guides the picker; typed values aren't blocked by it
    if (new Date(scheduledAt).getTime() < Date.now()) {
      setSubmitError("The scheduled time is in the past.");
      return;
    }
    setSubmitError(null);
    try {
      const campaignId = await ensureCampaign();
      await scheduleCampaign(campaignId, new Date(scheduledAt).toISOString());
      onComplete(campaignId);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Failed to schedule campaign");
    }
  };

  const [minDateTime] = useState(() => toDateTimeLocalValue(new Date(Date.now() + 5 * 60 * 1000)));

  return (
    <div className="lw-step-review-send">
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-foreground">Review & send</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Double-check everything before launching your campaign.
        </p>
      </div>

      {/* Summary */}
      <div className="space-y-4 mb-6">
        {/* Email */}
        <ReviewSection title="Email content">
          <ReviewRow label="Email" value={data.selectedDocument?.name ?? "—"} />
          <ReviewRow label="Subject" value={data.subject} />
          {data.previewText && <ReviewRow label="Preview text" value={data.previewText} />}
        </ReviewSection>

        {/* Details */}
        <ReviewSection title="Campaign details">
          <ReviewRow label="Name" value={data.name} />
          <ReviewRow label="Type" value={getTypeLabel(data.type)} />
          <ReviewRow label="From" value={`${data.fromName} <${data.fromEmail}>`} />
          {data.replyToEmail && <ReviewRow label="Reply-to" value={data.replyToEmail} />}
        </ReviewSection>

        {/* Recipients */}
        <ReviewSection title="Recipients">
          <ReviewRow label="Estimated" value={`${data.estimatedRecipients.toLocaleString()} contacts`} />
          <ReviewRow
            label="Lists"
            value={`${data.selectedListIds.length} list${data.selectedListIds.length !== 1 ? "s" : ""}`}
          />
          <ReviewRow
            label="Tracking"
            value={[data.trackOpens && "Opens", data.trackClicks && "Clicks"].filter(Boolean).join(", ") || "None"}
          />
        </ReviewSection>
      </div>

      {/* Error */}
      {(submitError ?? error) && (
        <div className="mb-4 rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
          {submitError ?? error}
        </div>
      )}

      {/* Schedule picker */}
      {scheduleMode && (
        <div className="mb-4 rounded-lg border border-border bg-card p-4">
          <label className="block text-sm font-medium text-foreground mb-2">Schedule for</label>
          <input
            type="datetime-local"
            value={scheduledAt}
            min={minDateTime}
            onChange={(e) => setScheduledAt(e.target.value)}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
      )}

      {/* Action buttons */}
      <div className="space-y-2">
        {!scheduleMode ? (
          <>
            <Button
              className="w-full"
              onClick={handleSendNow}
              disabled={isSaving || !documentId}
            >
              {isSaving ? "Sending…" : "Send Now"}
            </Button>
            <button
              onClick={() => setScheduleMode(true)}
              disabled={isSaving}
              className="w-full rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-accent transition-colors disabled:opacity-50"
            >
              Schedule for Later
            </button>
            <button
              onClick={handleSaveAsDraft}
              disabled={isSaving}
              className="w-full rounded-md px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors disabled:opacity-50"
            >
              Save as Draft
            </button>
          </>
        ) : (
          <>
            <Button
              className="w-full"
              onClick={handleSchedule}
              disabled={isSaving || !scheduledAt}
            >
              {isSaving ? "Scheduling…" : "Confirm Schedule"}
            </Button>
            <button
              onClick={() => setScheduleMode(false)}
              disabled={isSaving}
              className="w-full rounded-md px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
          </>
        )}
      </div>

      {/* Back */}
      <div className="mt-4">
        <button
          onClick={onBack}
          disabled={isSaving}
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>
      </div>
    </div>
  );
}

function ReviewSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">{title}</h3>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function ReviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-3 text-sm">
      <span className="w-24 shrink-0 text-muted-foreground">{label}</span>
      <span className="text-foreground">{value}</span>
    </div>
  );
}

/**
 * Formats a date as a `datetime-local` input value. Those values are in the user's local time,
 * so `toISOString()` (UTC) would be off by the timezone offset.
 */
function toDateTimeLocalValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}
